import React from 'react';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import { useT } from '../../i18n';

/**
 * ¿Los cobros de la oficina comparten los clientes? Dos opciones con su explicación, para que quien
 * configura la oficina entienda qué cambia antes de elegir.
 */
interface ShareClientsChoiceProps {
  value: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}

const ShareClientsChoice: React.FC<ShareClientsChoiceProps> = ({ value, onChange, disabled = false }) => {
  const t = useT();
  const option = (shared: boolean, title: string, body: string, example: string) => {
    const selected = value === shared;
    return (
      <Box
        component="label"
        data-tour={shared ? 'share-clients-yes' : 'share-clients-no'}
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'flex-start',
          p: 1.5,
          minWidth: 0,
          borderRadius: 1,
          border: 1,
          borderColor: selected ? 'primary.main' : 'divider',
          bgcolor: selected ? 'action.selected' : 'transparent',
          cursor: disabled ? 'default' : 'pointer',
        }}
      >
        <Radio value={shared ? 'yes' : 'no'} disabled={disabled} sx={{ p: 0.5, mt: -0.25 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2">{title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {body}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
            {example}
          </Typography>
        </Box>
      </Box>
    );
  };
  return (
    <FormControl component="fieldset" fullWidth disabled={disabled} data-tour="share-clients">
      <FormLabel component="legend" sx={{ mb: 0.5 }}>
        {t('offices.shareClients.question')}
      </FormLabel>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t('offices.shareClients.intro')}
      </Typography>
      <RadioGroup value={value ? 'yes' : 'no'} onChange={(_, v) => onChange(v === 'yes')}>
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' } }}>
          {option(false, t('offices.shareClients.noTitle'), t('offices.shareClients.noBody'), t('offices.shareClients.noExample'))}
          {option(true, t('offices.shareClients.yesTitle'), t('offices.shareClients.yesBody'), t('offices.shareClients.yesExample'))}
        </Box>
      </RadioGroup>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
        {t('offices.shareClients.change')}
      </Typography>
    </FormControl>
  );
};

export default ShareClientsChoice;
